<template>
  <div
    class="relative mb-16 w-full overflow-hidden md:mb-0 md:block md:w-full md:min-w-[337px] md:max-w-[498px] md:overflow-visible"
  >
    <!-- Mobile: GalleryNew-style carousel (big primary + scrollable thumbnails) -->
    <div class="md:hidden">
      <ReviewPhotosGuidanceDrawer
        v-if="defaultImageUrl"
        :grades="gradesWithPictures"
        :initial-grade="selectedGradeLabel ?? gradesWithPictures[0]?.gradeLabel"
        :target-grade
        :target-picture-index
        :name="REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME"
        :title="i18n(translations.drawerTitle)"
      >
        <template #trigger="{ open }">
          <div
            class="w-[calc(100vw-48px)] max-w-full overflow-hidden"
            :data-two-images="mobileItems.length === 2 || undefined"
            data-test="review-photos-guidance-pair"
          >
            <GalleryCarousel
              :items="mobileItems.length > 1 ? mobileItems : [mobileItems[0]]"
              :primary-image-wrapper-classes="
                isTwoImages
                  ? 'pl-6 w-[max(calc(80vw-24px),214px)]'
                  : 'pl-6 w-[max(calc(80vw-24px),214px)] max-w-[calc(var(--carousel-max-size,45lvh)+12px)]'
              "
              :product-name="trackingModel"
              :secondary-list-classes="
                isTwoImages
                  ? '!grid-flow-col !auto-cols-[max(calc(80vw-24px),214px)]'
                  : `${CAROUSEL_MAX_HEIGHT_CLASS} ![grid-template-rows:repeat(2,auto)] !pr-24`
              "
            >
              <template #first="{ items }">
                <div
                  class="relative aspect-square w-full cursor-pointer"
                  :class="
                    !isTwoImages &&
                    'max-h-[calc(var(--carousel-max-size,45lvh)-12px)] max-w-[calc(var(--carousel-max-size,45lvh)-12px)]'
                  "
                  role="button"
                  tabindex="0"
                  @click="handleReviewClick(open, 0)"
                  @keydown.enter.prevent="handleReviewClick(open, 0)"
                >
                  <RevIllustration
                    v-if="items[0]?.src"
                    alt=""
                    class="rounded-lg h-full w-full object-cover"
                    :height="498"
                    :src="items[0].src"
                    :width="498"
                  />
                  <div class="absolute inset-x-0 bottom-0 p-8">
                    <RevTag
                      class="bg-overlay-low text-static-light-hi"
                      :label="exampleTag"
                      variant="secondary"
                    />
                  </div>
                </div>
              </template>
              <template #rest="{ items }">
                <template v-if="isPendingReviewsPerGrade">
                  <li
                    v-for="n in 4"
                    :key="`skeleton-${n}`"
                    class="shrink-0 [grid-row:span_1] [grid-column:span_1]"
                    :class="SMALL_ALT_ITEM_SIZE_CLASS"
                  >
                    <RevSkeleton
                      height="130px"
                      shape="rectangle"
                      width="130px"
                    />
                  </li>
                  <li aria-hidden="true" class="w-[80px] shrink-0" />
                </template>
                <template v-else>
                  <li
                    v-for="(item, index) in items"
                    v-show="index !== 0"
                    :key="item.id || item.src"
                    class="shrink-0 cursor-pointer [grid-row:span_1] [grid-column:span_1] md:[grid-row:initial] md:[grid-column:initial]"
                    :class="
                      isTwoImages
                        ? 'w-[max(calc(80vw-24px),214px)]'
                        : SMALL_ALT_ITEM_SIZE_CLASS
                    "
                    role="button"
                    tabindex="0"
                    @click="handleReviewClick(open, index)"
                    @keydown.enter.prevent="handleReviewClick(open, index)"
                  >
                    <div
                      class="rounded-lg relative aspect-square h-full w-full overflow-hidden"
                      data-test="review-photos-guidance-slide"
                    >
                      <RevIllustration
                        :alt="item.alt || ''"
                        class="rounded-lg h-full w-full object-cover"
                        :height="isTwoImages ? 498 : 130"
                        :src="item.src"
                        :width="isTwoImages ? 498 : 130"
                      />
                      <div
                        class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                      >
                        <RevTag
                          v-if="item.customerName"
                          class="self-start"
                          :label="item.customerName"
                          variant="secondary"
                        />
                        <span
                          v-if="item.reviewDate"
                          class="text-static-light-hi body-1-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                        >
                          {{ item.reviewDate }}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li
                    v-if="!isTwoImages"
                    aria-hidden="true"
                    class="w-[80px] shrink-0 md:hidden"
                  />
                </template>
              </template>
            </GalleryCarousel>
          </div>
        </template>
      </ReviewPhotosGuidanceDrawer>

      <div
        v-else-if="isPendingReviewsPerGrade"
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <RevSpinner size="large" />
      </div>
    </div>

    <!-- Desktop: default image (the review photos carousel is rendered
         under the grade picker via the step-end-grades slot) -->
    <div class="hidden md:block">
      <div
        v-if="defaultImageUrl"
        class="rounded-lg relative flex w-full aspect-square cursor-pointer overflow-hidden md:rounded-[32px]"
        data-test="review-photos-guidance-default-image"
        role="button"
        tabindex="0"
        @click="handleReviewClick(openDesktopDrawer, 0)"
        @keydown.enter.prevent="handleReviewClick(openDesktopDrawer, 0)"
      >
        <RevIllustration
          alt=""
          class="rounded-lg h-full w-full object-cover md:rounded-[32px]"
          :height="498"
          :src="defaultImageUrl"
          :width="498"
        />
        <div class="absolute inset-x-0 bottom-0 p-16">
          <RevTag
            class="bg-overlay-low text-static-light-hi"
            :label="exampleTag"
            variant="secondary"
          />
        </div>
      </div>

      <div
        v-if="!defaultImageUrl && isPendingReviewsPerGrade"
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <RevSpinner size="large" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag } from '@ds/components/Tag'

import {
  CAROUSEL_MAX_HEIGHT_CLASS,
  SMALL_ALT_ITEM_SIZE_CLASS,
} from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.constants'
import type { GalleryImage } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import GalleryCarousel from '~/scopes/product-core/components/presentationals/GalleryNew/components/Carousel/Carousel.vue'
import type { ReviewsPerGrade } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.types'

import translations from './ReviewPhotosGuidance.translations'
import ReviewPhotosGuidanceDrawer from './components/ReviewPhotosGuidanceDrawer/ReviewPhotosGuidanceDrawer.vue'
import {
  REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME,
  useReviewPhotosGuidance,
} from './composables/useReviewPhotosGuidance'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    categoryId?: string
    reviewsPerGrade?: ReviewsPerGrade | null
    isPendingReviewsPerGrade?: boolean
  }>(),
  {
    trackingModel: '',
    categoryId: undefined,
    reviewsPerGrade: null,
    isPendingReviewsPerGrade: false,
  },
)

const i18n = useI18n()

const {
  targetGrade,
  targetPictureIndex,
  selectedGradeLabel,
  defaultImageUrl,
  gradesWithPictures,
  reviewSlides,
  exampleTag,
  handleReviewClick,
} = useReviewPhotosGuidance(props)

function openDesktopDrawer() {
  openModal(REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME)
}

const mobileItems = computed<
  (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[]
>(() => {
  if (!defaultImageUrl.value) return []

  const items: (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[] = [
    {
      id: 'default-grade-image',
      src: defaultImageUrl.value,
      alt: '',
      customerName: null,
      reviewDate: null,
    },
  ]

  return [
    ...items,
    ...reviewSlides.value.map((slide) => ({
      id: slide.id,
      src: slide.imageUrl,
      alt: slide.title,
      customerName: slide.customerName,
      reviewDate: slide.reviewDate,
    })),
  ]
})

const isTwoImages = computed(() => mobileItems.value.length === 2)
</script>
